import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Empty, Spin, Alert, Button } from "antd";
import { ReloadOutlined, CreditCardOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SettingsLayout from "../layout/SettingsLayout";
import { apiService } from "../../services/api";

interface Receipt {
  id: string;
  orderId?: string;
  serviceId?: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod?: string;
  provider: string;
  eventType: string;
  timestamp: string;
  periodStart?: string;
  periodEnd?: string;
}

function formatAmount(amount: number, currency: string): string {
  const major = amount / 100;
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: currency || "MXN",
    minimumFractionDigits: 2,
  }).format(major);
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatShortDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const STATUS_COLOR: Record<string, string> = {
  paid: "green",
  PAID: "green",
  succeeded: "green",
  SUCCEEDED: "green",
  failed: "red",
  FAILED: "red",
  cancelled: "default",
  refunded: "orange",
  pending: "blue",
  PENDING: "blue",
};

const BillingHistory: React.FC = () => {
  const { t } = useTranslation();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchReceipts = useCallback(async (token?: string) => {
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (token) params.set("nextToken", token);
      const data = await apiService.get<{ receipts: Receipt[]; nextToken?: string }>(
        `/billing/receipts?${params}`
      );
      if (token) {
        setReceipts((prev) => [...prev, ...(data.receipts ?? [])]);
      } else {
        setReceipts(data.receipts ?? []);
      }
      setNextToken(data.nextToken ?? null);
    } catch (err: any) {
      setError(err.message || t("billing.errorLoading"));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [t]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleLoadMore = () => {
    if (!nextToken) return;
    setLoadingMore(true);
    fetchReceipts(nextToken);
  };

  const handleRefresh = () => {
    setLoading(true);
    setError(null);
    setNextToken(null);
    fetchReceipts();
  };

  const columns = [
    {
      title: t("billing.date"),
      dataIndex: "timestamp",
      key: "timestamp",
      render: (v: string) => formatDate(v),
      width: 180,
    },
    {
      title: t("billing.amount"),
      key: "amount",
      render: (_: any, r: Receipt) => (
        <span className="font-mono font-medium">{formatAmount(r.amount, r.currency)}</span>
      ),
      width: 130,
    },
    {
      title: t("billing.status"),
      dataIndex: "status",
      key: "status",
      render: (s: string) => (
        <Tag color={STATUS_COLOR[s] ?? "default"}>{s?.toUpperCase()}</Tag>
      ),
      width: 110,
    },
    {
      title: t("billing.type"),
      dataIndex: "eventType",
      key: "eventType",
      render: (v: string) => {
        const labels: Record<string, string> = {
          checkout: t("billing.typeCheckout"),
          renewal: t("billing.typeRenewal"),
          refund: t("billing.typeRefund"),
        };
        return labels[v] ?? v;
      },
      width: 120,
    },
    {
      title: t("billing.method"),
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (v?: string) => v ?? "—",
      width: 120,
    },
    {
      title: t("billing.period"),
      key: "period",
      render: (_: any, r: Receipt) => {
        if (!r.periodStart) return "—";
        return (
          <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
            {formatShortDate(r.periodStart)} – {formatShortDate(r.periodEnd)}
          </span>
        );
      },
    },
    {
      title: t("billing.serviceId"),
      dataIndex: "serviceId",
      key: "serviceId",
      render: (v?: string) =>
        v ? (
          <Link
            to={`/services/${v}`}
            className="font-mono text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            {v.length > 20 ? `${v.slice(0, 20)}…` : v}
          </Link>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <SettingsLayout>
      <div>
        {/* Header */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-1 h-10 bg-gray-900 dark:bg-white" />
              <h1 className="text-2xl font-normal text-gray-900 dark:text-white">
                {t("billing.title")}
              </h1>
            </div>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={loading}>
              {t("common.refresh")}
            </Button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t("billing.subtitle")}</p>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {error && (
            <div className="p-6">
              <Alert type="error" message={error} showIcon />
            </div>
          )}

          {loading && !error ? (
            <div className="flex items-center justify-center py-20">
              <Spin size="large" />
            </div>
          ) : !error && receipts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <CreditCardOutlined className="text-5xl text-gray-300" />
              <Empty description={t("billing.empty")} />
            </div>
          ) : !error ? (
            <>
              <Table
                columns={columns}
                dataSource={receipts}
                rowKey="id"
                pagination={false}
                scroll={{ x: 900 }}
                size="middle"
                className="billing-table"
              />
              {nextToken && (
                <div className="flex justify-center py-4 border-t border-gray-100 dark:border-gray-700">
                  <Button onClick={handleLoadMore} loading={loadingMore}>
                    {t("billing.loadMore")}
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </SettingsLayout>
  );
};

export default BillingHistory;
